import React, { useEffect, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import herings_logo_b from "../../assets/images/etc/heringsLOGO_black.svg";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "../../i18n";
import "./NavbarV2.css";

const STORAGE_KEY = "lang";

const getLangFromPath = (pathname) => {
  const m = pathname.match(/^\/(kr|en)(\/|$)/);
  return m ? m[1] : DEFAULT_LANG;
};

const swapLang = (pathname, nextLang) => {
  const m = pathname.match(/^\/(kr|en)(\/.*)?$/);
  const tail = m ? m[2] || "" : pathname;
  return `/${nextLang}${tail || ""}` || `/${nextLang}`;
};

/**
 * Redesigned navigation — sticky, translucent, condenses on scroll.
 * IA: Home · About · Solutions · Products · News · Contact
 * NOTE: links point to the existing live pages until each is redesigned;
 * Home points to the new /v2 homepage.
 */
export default function NavbarV2() {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const pathname = location.pathname;
  const lang = getLangFromPath(pathname);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV = [
    { key: "home", label: t("homeV2.nav.home"), to: `/${lang}/v2` },
    { key: "about", label: t("homeV2.nav.about"), to: `/${lang}/whoweare/` },
    { key: "solutions", label: t("homeV2.nav.solutions"), to: `/${lang}/rpmsolution/` },
    { key: "products", label: t("homeV2.nav.products"), to: `/${lang}/v2`, hash: "#v2-products" },
    { key: "news", label: t("homeV2.nav.news"), to: `/${lang}/news/` },
    { key: "contact", label: t("homeV2.nav.contact"), to: `/${lang}/contactus/` },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const changeLang = (next) => {
    if (next === lang) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {}
    history.push(swapLang(pathname, next));
  };

  const go = (item) => {
    setOpen(false);
    if (item.hash && (pathname === `/${lang}/v2` || pathname === `/${lang}/v2/`)) {
      const el = document.querySelector(item.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    history.push(item.to + (item.hash || ""));
  };

  const LangSwitch = () => (
    <div className="v2nav-lang">
      {SUPPORTED_LANGS.map((lng, i) => (
        <React.Fragment key={lng}>
          {i > 0 && <span className="v2nav-lang-sep">|</span>}
          <button
            type="button"
            className={`v2nav-lang-btn ${lng === lang ? "is-active" : ""}`}
            onClick={() => changeLang(lng)}
          >
            {t(`common.lang.${lng}`)}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <header className={`v2nav ${scrolled ? "is-scrolled" : ""}`}>
      <div className="v2nav-inner">
        <NavLink to={`/${lang}/v2`} className="v2nav-logo" onClick={() => setOpen(false)}>
          <img src={herings_logo_b} alt="HERINGS" />
        </NavLink>

        <nav className="v2nav-links">
          {NAV.map((item) => (
            <button key={item.key} className="v2nav-link" onClick={() => go(item)}>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="v2nav-right">
          <LangSwitch />
          <button
            className="v2nav-cta"
            onClick={() => history.push(`/${lang}/contactus/`)}
          >
            {t("homeV2.nav.getInTouch")}
          </button>
        </div>

        <button
          className={`v2nav-burger ${open ? "is-open" : ""}`}
          aria-label="Menu"
          onClick={() => setOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className={`v2nav-mobile ${open ? "is-open" : ""}`}>
        <nav className="v2nav-mobile-links">
          {NAV.map((item) => (
            <button key={item.key} className="v2nav-mobile-link" onClick={() => go(item)}>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="v2nav-mobile-footer">
          <LangSwitch />
          <button
            className="v2nav-cta v2nav-cta-full"
            onClick={() => {
              setOpen(false);
              history.push(`/${lang}/contactus/`);
            }}
          >
            {t("homeV2.nav.getInTouch")}
          </button>
        </div>
      </div>
    </header>
  );
}
